/**
 * <product-media>
 *
 * Owns the interactive media in a product gallery: native video, external
 * (YouTube) video, and 3D models. Shopify does not render controls for these.
 * The Shopify media libraries build them at runtime, so each media element must
 * be upgraded by script after it enters the DOM.
 *
 * This is a custom element, not a one-time global init, because a colour swatch
 * swap replaces the whole `<product-info>` subtree. `connectedCallback` runs on
 * the new subtree, so the swapped-in media is upgraded without a separate hook.
 * `disconnectedCallback` destroys the players the outgoing subtree held.
 */
class ProductMedia extends HTMLElement {
  /** Media types that need a runtime player. Images need none. */
  static INTERACTIVE_TYPES = ['video', 'external_video', 'model'];

  /** Shopify media libraries this element needs before it can build a player. */
  static FEATURES = [
    { name: 'model-viewer-ui', version: '1.0' },
    { name: 'shopify-xr', version: '1.0' },
    { name: 'video-ui', version: '1.0' },
  ];

  #abortController = null;

  /** Players built by this element, keyed by media id. */
  #instances = new Map();

  /** The instance that is playing now, so a new play can pause it. */
  #activeInstance = null;

  /** Set in disconnectedCallback, so an in-flight async init stops. */
  #disconnected = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#disconnected = false;

    const elements = this.querySelectorAll('[data-interactive]');
    if (!elements.length) return;

    this.#loadFeatures().then(() => {
      if (this.#disconnected) return;
      elements.forEach((element) => this.#initElement(element));
    });
  }

  disconnectedCallback() {
    this.#disconnected = true;
    this.#abortController?.abort();
    this.#abortController = null;
    this.destroy();
  }

  /**
   * Resolve when the Shopify media libraries are ready.
   *
   * External video also needs the YouTube iframe API. That API reports itself
   * through the global `onYouTubeIframeAPIReady` callback, which only fires
   * once per page. An element that connects after it has fired must read
   * `YT.loaded` instead, or it waits for a callback that never comes.
   *
   * @returns {Promise<void>}
   */
  #loadFeatures() {
    return new Promise((resolve) => {
      if (!window.Shopify?.loadFeatures) {
        resolve();
        return;
      }

      window.Shopify.loadFeatures(ProductMedia.FEATURES, () => {
        if (window.YT?.loaded) {
          resolve();
          return;
        }

        // Chain onto any existing handler so a second <product-media> on the
        // page does not overwrite the first one's callback.
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof previous === 'function') previous();
          resolve();
        };
      });
    });
  }

  /**
   * Build the player for one `.media--product` container.
   *
   * @param {HTMLElement} element - The `[data-interactive]` media container.
   */
  #initElement(element) {
    const { mediaId, mediaType } = element.dataset;
    if (!mediaId || !ProductMedia.INTERACTIVE_TYPES.includes(mediaType)) return;
    if (this.#instances.has(mediaId)) return;

    // The media tag is the container's first child: `video_tag`,
    // `external_video_tag`, and `model_viewer_tag` each render one root node.
    const media = element.firstElementChild;
    if (!media) return;

    const instance = { id: mediaId, type: mediaType, container: element, media };

    switch (mediaType) {
      case 'video':
        this.#initVideo(instance, element);
        break;
      case 'external_video':
        this.#initExternalVideo(instance);
        break;
      case 'model':
        this.#initModel(instance, element);
        break;
    }

    this.#instances.set(mediaId, instance);
  }

  /**
   * Upgrade a native video into a Plyr player. Plyr builds the custom control
   * bar, which the Liquid markup does not contain.
   *
   * @param {object} instance - The media instance to populate.
   * @param {HTMLElement} element - The media container, read for `data-loop`.
   */
  #initVideo(instance, element) {
    if (!window.Shopify?.Plyr) return;

    instance.player = new window.Shopify.Plyr(instance.media, {
      loop: { active: element.dataset.loop === 'true' },
    });

    instance.player.on('playing', () => {
      this.pauseActiveMedia(instance);
      this.#activeInstance = instance;
    });
  }

  /**
   * @param {object} instance - The media instance to populate.
   */
  #initExternalVideo(instance) {
    if (!window.YT?.Player) return;

    instance.player = new window.YT.Player(instance.media);
    instance.player.addEventListener('onStateChange', (event) => {
      // 1 is YT.PlayerState.PLAYING.
      if (event.data !== 1) return;
      this.pauseActiveMedia(instance);
      this.#activeInstance = instance;
    });
  }

  /**
   * @param {object} instance - The media instance to populate.
   * @param {HTMLElement} element - The media container, searched for the poster.
   */
  #initModel(instance, element) {
    const viewer = element.querySelector('model-viewer');
    if (!viewer || !window.Shopify?.ModelViewerUI) return;

    instance.viewer = new window.Shopify.ModelViewerUI(viewer);

    const poster = element.querySelector('.model-poster');
    if (!poster) return;

    poster.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#playModel(instance);
      },
      { signal: this.#abortController.signal }
    );
  }

  /**
   * @param {object} instance - The model instance to play.
   */
  #playModel(instance) {
    this.pauseActiveMedia(instance);
    instance.viewer.play();
    instance.container.classList.add('model-active');
    this.#activeInstance = instance;

    setTimeout(() => {
      instance.container.querySelector('model-viewer')?.focus();
    }, 300);
  }

  /**
   * Pause whichever media is playing, so two never play at once.
   *
   * Public: the lightbox calls this when it opens.
   *
   * @param {object} [instance] - Media that is about to play. When this is the
   *   media that already plays, nothing is paused.
   */
  pauseActiveMedia(instance) {
    const active = this.#activeInstance;
    if (!active || active === instance) return;

    if (active.player) {
      if (active.type === 'video') active.player.pause();
      else if (active.type === 'external_video') active.player.pauseVideo();
    } else if (active.viewer) {
      active.container.classList.remove('model-active');
      active.viewer.pause();
    }

    this.#activeInstance = null;
  }

  /**
   * Destroy every player this element built.
   *
   * Plyr and the YouTube player both attach listeners outside this subtree, so
   * removing the DOM alone leaks them. A swatch swap removes this element on
   * every colour change, so this runs often.
   */
  destroy() {
    this.#instances.forEach((instance) => {
      try {
        instance.player?.destroy?.();
        instance.viewer?.destroy?.();
      } catch {
        // A player can already be torn down when its DOM went away first.
        // There is nothing to recover, and nothing left to release.
      }
    });

    this.#instances.clear();
    this.#activeInstance = null;
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia);
}
